import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, Textarea,
  FormControl, FormLabel, Box, Text, Badge, useToast, Flex, IconButton,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import api from '../api';
import FileUpload from './FileUpload';

export default function NotesModal({ projectId, taskId, isOpen, onClose }) {
  const [notes, setNotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    subject: '', body: '', note_date: new Date().toISOString().split('T')[0], entity_type: 'client',
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const toast = useToast();

  const fetchNotes = async () => {
    try {
      const taskParam = taskId ? `&task_id=${taskId}` : '';
      const { data } = await api.get(`/notes?project_id=${projectId}${taskParam}`);
      setNotes(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (isOpen && projectId) {
      fetchNotes();
      setShowForm(false);
    }
  }, [isOpen, projectId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/notes/${editingId}`, { ...form });
      } else {
        await api.post('/notes', { ...form, project_id: projectId, task_id: taskId || null });
      }
      await fetchNotes();
      setShowForm(false); setEditingId(null);
      setForm({ subject: '', body: '', note_date: new Date().toISOString().split('T')[0], entity_type: 'client' });
      toast({ title: editingId ? 'Note updated' : 'Note added', status: 'success', duration: 1500 });
    } catch (err) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (note) => {
    setForm({
      subject: note.subject || '', body: note.body || '',
      note_date: note.note_date ? new Date(note.note_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      entity_type: note.entity_type || 'client',
    });
    setEditingId(note.id); setShowForm(true);
  };

  const openNew = () => {
    setForm({ subject: '', body: '', note_date: new Date().toISOString().split('T')[0], entity_type: 'client' });
    setEditingId(null); setShowForm(!showForm);
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader fontSize="md">
          <Flex align="center" justify="space-between" pr={8}>
            Notes
            <Button size="xs" leftIcon={<FiPlus />} onClick={openNew}
              variant={showForm && !editingId ? 'solid' : 'outline'} colorScheme="purple">
              {showForm && !editingId ? 'Cancel' : 'New'}
            </Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="purple.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Date</FormLabel>
                    <Input size="xs" type="date" value={form.note_date}
                      onChange={(e) => setForm(p => ({ ...p, note_date: e.target.value }))} />
                  </FormControl>
                  <FormControl flex={2}>
                    <FormLabel fontSize="xs" mb={0.5}>Subject</FormLabel>
                    <Input size="xs" value={form.subject}
                      onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))} />
                  </FormControl>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                    <Select size="xs" fontSize="11px" value={form.entity_type}
                      onChange={(e) => setForm(p => ({ ...p, entity_type: e.target.value }))}>
                      <option value="client">Client</option>
                      <option value="supplier">Supplier</option>
                    </Select>
                  </FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Content</FormLabel>
                  <Textarea size="xs" rows={4} value={form.body}
                    onChange={(e) => setForm(p => ({ ...p, body: e.target.value }))} />
                </FormControl>
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">
                  {editingId ? 'Update Note' : 'Save Note'}
                </Button>
              </VStack>
            </Box>
          )}

          {notes.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
              No notes recorded yet.
            </Text>
          ) : (
            <Accordion allowMultiple>
              {notes.map(note => (
                <AccordionItem key={note.id} border="1px" borderColor="gray.100" borderRadius="md" mb={2}>
                  <AccordionButton py={2} px={3}>
                    <Flex flex={1} align="center" gap={2} fontSize="12px">
                      <Text fontWeight="500" color="gray.600" w="80px" flexShrink={0}>
                        {new Date(note.note_date).toLocaleDateString('en-GB')}
                      </Text>
                      <Text fontWeight="500" noOfLines={1}>{note.subject || '(no subject)'}</Text>
                      {note.entity_type === 'supplier' && (
                        <Badge fontSize="8px" colorScheme="orange" ml={1}>Supplier</Badge>
                      )}
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={3} px={3}>
                    <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap">
                      {note.body || '(no content)'}
                    </Text>
                    {note.created_by_name && (
                      <Text fontSize="10px" color="gray.400" mt={2}>By {note.created_by_name}</Text>
                    )}
                    <Flex justify="space-between" align="center" mt={2}>
                      <Box flex={1}>
                        <FileUpload entityType="note" entityId={note.id} folder="documents"
                          accept="image/*,.pdf,video/*" label="Attachments" />
                      </Box>
                      <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" colorScheme="blue"
                        onClick={(e) => { e.stopPropagation(); openEdit(note); }}
                        aria-label="Edit note" ml={2} />
                      <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm(note); setDeleteReason(''); }}
                        aria-label="Delete note" ml={2} />
                    </Flex>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Delete Confirmation */}
    {deleteConfirm && (
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} size="sm">
        <ModalOverlay /><ModalContent>
          <ModalHeader fontSize="md" color="red.600">Delete Note</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="12px" mb={2}>
              Delete "{deleteConfirm.subject || 'this note'}"?
            </Text>
            <Input size="sm" placeholder="Reason for deletion (optional)" value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)} />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button size="sm" colorScheme="red" onClick={async () => {
              try {
                await api.delete(`/notes/${deleteConfirm.id}`, { data: { reason: deleteReason || null } });
                toast({ title: 'Note deleted', status: 'info', duration: 1500 });
                setDeleteConfirm(null); setDeleteReason('');
                await fetchNotes();
              } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
            }}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}
    </>
  );
}
